/*
 * © 2026 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { Machine, ProcessAction } from "@centralwebteam/narwhal";
import { Routes } from "./session/routeDefinitions";
import { JobType } from "./state";

/** Machines referenced by a page's data, by id and/or name (some page data only carries names). */
export type MachineAssociations = { ids: Set<string>; names: Set<string> };

/** Data sources used to scope the Machine filter for each page that supports it. */
export type PageMachineData = {
  jobTypes: JobType[];
  processActions: ProcessAction[];
};

/** Machines that ran any of the loaded Job Performance jobs. */
export const getMachineIdsAndNamesFromJobTypes = (
  jobTypes: JobType[]
): MachineAssociations => ({
  ids: new Set(jobTypes.flatMap((job) => job.machines)),
  names: new Set(),
});

/** Loaded target and source machines across all Process Updates tabs. */
export const getMachineIdsAndNamesFromProcessActions = (
  processActions: ProcessAction[]
): MachineAssociations => {
  const associations: MachineAssociations = {
    ids: new Set(),
    names: new Set(),
  };
  processActions.forEach(({ details }) => {
    const machines = [details?.processTarget, details?.sourceMachine];
    machines.forEach((machine) => {
      if (machine?.id) associations.ids.add(machine.id);
      if (machine?.name) associations.names.add(machine.name);
    });
  });
  return associations;
};

/** Machine associations for the given page, or null when the page shows every machine. */
export const getMachineIdsAndNamesUsedByPage = (
  view: Routes,
  data: PageMachineData
): MachineAssociations | null => {
  switch (view) {
    case "job performance":
      return getMachineIdsAndNamesFromJobTypes(data.jobTypes);
    case "process updates":
      return getMachineIdsAndNamesFromProcessActions(data.processActions);
    default:
      return null;
  }
};

/** Restricts machines to those associated with page data, and locations to those containing
 * an associated machine (including ancestor locations so the tree stays connected). */
export const filterMachinesAndLocationsByMachineReferences = <
  L extends { id: string; parentId?: string | null },
>(
  locations: L[],
  machines: Machine[],
  associations: MachineAssociations | null
): { locations: L[]; machines: Machine[] } => {
  if (!associations) return { locations, machines };

  const associatedMachines = machines.filter(
    (m) => associations.ids.has(m.id) || associations.names.has(m.name)
  );

  const locationMap = new Map(locations.map((l) => [l.id, l]));
  const locationIds = new Set<string>();
  associatedMachines.forEach((machine) => {
    let location = locationMap.get(machine.locationId ?? "");
    while (location && !locationIds.has(location.id)) {
      locationIds.add(location.id);
      location = locationMap.get(location.parentId ?? "");
    }
  });

  return {
    locations: locations.filter((l) => locationIds.has(l.id)),
    machines: associatedMachines,
  };
};
